import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { 
  CheckCircle2, 
  Info, 
  AlertCircle, 
  Quote, 
  ArrowRight, 
  Plus, 
  Minus, 
  Brain, 
  Zap, 
  Target, 
  Clock, 
  Calendar, 
  Phone, 
  ShieldCheck, 
  EyeOff, 
  MessageSquareOff, 
  Coffee, 
  Sliders, 
  Sparkles, 
  MapPin, 
  Laptop, 
  HelpCircle,
  Coins,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="bg-white border border-[#E8DDCE] rounded-xl overflow-hidden transition-all duration-300">
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-[#FAF7F2] transition-colors group cursor-pointer"
        aria-expanded={isOpen}
      >
        <span className="font-bold text-[#123F13] md:text-lg pr-8">{question}</span>
        <div className={`shrink-0 text-[#123F13] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-[#3B352F]/70 leading-relaxed border-t border-[#FAF7F2] pt-4">
              <div className="space-y-4">
                {answer}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Autisme = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // 10 Store Visuelle FAQ-svar med interne links
  const faqData = [
    {
      question: "Er det her behandling af autisme?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Nej</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Autisme skal ikke væk</div>
              <p className="text-xs text-[#3B352F]/80">Fokus er ikke at fjerne autisme.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Fokus er trivsel</div>
              <p className="text-xs text-[#3B352F]/80">Vi ser på belastning, angst, sansepres, struktur og energi.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Hverdagen skal fungere</div>
              <p className="text-xs text-[#3B352F]/80">Vi arbejder med konkrete strategier, der kan gøre hverdagen mere bæredygtig.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Autisme er ikke noget, der skal behandles væk. Fokus er på <strong>trivsel, belastning, angst, sansepres, struktur, energi</strong> og <strong>konkrete strategier</strong>, der kan gøre hverdagen mere bæredygtig.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/metakognitiv-terapi" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Metakognitiv terapi &rarr;
            </Link>
            <Link to="/hypnose" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Hypnose &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Skal jeg have en diagnose?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Nej</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du er velkommen</div>
              <p className="text-xs text-[#3B352F]/80">Du behøver ikke være udredt.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Autistiske træk</div>
              <p className="text-xs text-[#3B352F]/80">Eller mistanke om autisme er også nok.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Din hverdag er udgangspunktet</div>
              <p className="text-xs text-[#3B352F]/80">Vi tager udgangspunkt i det, du oplever.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Du kan også komme, hvis du har <strong>autistiske træk</strong>, <strong>mistanke om autisme</strong> eller oplever belastning, der minder om det, der beskrives her.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/om-lars" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Om Lars &rarr;
            </Link>
            <Link to="/kontakt" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Spørg mig &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan jeg bare booke én session?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Ja</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Start med én session</div>
              <p className="text-xs text-[#3B352F]/80">Du kan begynde med en første behandlingssession.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Vi skaber overblik</div>
              <p className="text-xs text-[#3B352F]/80">Vi see på din situation og det, du ønsker hjælp til.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Næste skridt vurderes sammen</div>
              <p className="text-xs text-[#3B352F]/80">Vi vurderer, om én session er nok, eller om et forløb giver mening.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Ja. Du kan starte med en <strong>første behandlingssession til 375 kr.</strong> Her skaber vi overblik og <strong>vurderer sammen</strong>, om én session er nok, eller om et forløb giver mening.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/bestil-tid" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Book første session &rarr;
            </Link>
            <Link to="/priser" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Se priser &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Hvad hvis jeg bliver overstimuleret i sessionen?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Så tager vi højde for det</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Du må gerne sige til</div>
              <p className="text-xs text-[#3B352F]/80">Vi tilpasser sessionen, når noget bliver for meget.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Vi sænker tempoet</div>
              <p className="text-xs text-[#3B352F]/80">Vi kan bruge kortere spørgsmål og et roligere tempo.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Vi bruger redskaber</div>
              <p className="text-xs text-[#3B352F]/80">Pauser, skalaer og visuelle redskaber kan hjælpe.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Så tager vi højde for det. Vi kan bruge <strong>pauser, kortere spørgsmål, skalaer, visuelle redskaber</strong> og et <strong>roligere tempo</strong>.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/stress" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Stress og belastning &rarr;
            </Link>
            <Link to="/online-sessioner" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Online sessioner &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Skal jeg kunne tale om følelser?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Nej</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Ikke på en bestemt måde</div>
              <p className="text-xs text-[#3B352F]/80">Du behøver ikke tale om følelser på en bestemt måde.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Vi kan arbejde konkret</div>
              <p className="text-xs text-[#3B352F]/80">Vi kan tale om situationer, belastning, tanker, energi og behov.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Det må gerne være enkelt</div>
              <p className="text-xs text-[#3B352F]/80">Vi gør det ikke mere følelsesmæssigt end nødvendigt.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Nej, ikke på en bestemt måde. Vi kan <strong>arbejde konkret</strong> med situationer, belastning, tanker, energi og behov uden at gøre det mere følelsesmæssigt end nødvendigt.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/metakognitiv-terapi" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Metakognitiv terapi &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan jeg få ting skrevet ned bagefter?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Ja</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Skriftlige pointer</div>
              <p className="text-xs text-[#3B352F]/80">Vi kan skrive de vigtigste pointer ned.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Korte øvelser</div>
              <p className="text-xs text-[#3B352F]/80">Du kan få enkle øvelser med, hvis det giver mening.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">En enkel opsummering</div>
              <p className="text-xs text-[#3B352F]/80">Så du ikke skal huske alt fra samtalen.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Ja, vi kan aftale <strong>skriftlige pointer, korte øvelser</strong> eller <strong>en enkel opsummering</strong>, så du ikke skal huske alt fra samtalen.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/bestil-tid" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Første behandlingssession &rarr;
            </Link>
            <Link to="/kontakt" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Kontakt mig &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Bruger du hypnose?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Kun hvis det giver mening</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Det er valgfrit</div>
              <p className="text-xs text-[#3B352F]/80">Hypnose bruges kun, hvis det passer til dig.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Rolig guidning</div>
              <p className="text-xs text-[#3B352F]/80">Hypnose bruges som rolig guidning og fokuseret opmærksomhed.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Kan fravælges</div>
              <p className="text-xs text-[#3B352F]/80">Det kan også helt vælges fra.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Kun hvis det giver mening for dig. Hypnose bruges <strong>ikke mystisk eller pressende</strong>, men som <strong>rolig guidning</strong> og fokuseret opmærksomhed. Det kan også <strong>helt fravælges</strong>.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/hypnose" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Læs om hypnose &rarr;
            </Link>
            <Link to="/metakognitiv-terapi" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Metakognitiv terapi &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Kan pårørende være med?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Det kan aftales</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Tryghed først</div>
              <p className="text-xs text-[#3B352F]/80">Det kan give mere tryghed for nogle.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Mere overblik</div>
              <p className="text-xs text-[#3B352F]/80">Det kan også hjælpe med struktur og overblik.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Det tilpasses dig</div>
              <p className="text-xs text-[#3B352F]/80">For andre er det bedst at tale alene.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Det kan aftales, hvis det skaber <strong>mere tryghed</strong> eller hjælper med <strong>overblik</strong>. For andre er det bedst at tale alene. Det tilpasses behovet.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/kontakt" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Kontakt mig &rarr;
            </Link>
            <Link to="/bestil-tid" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Første behandlingssession &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Hvad hvis jeg har autistisk burnout?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Så tager vi det nænsomt</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Nænsomt tempo</div>
              <p className="text-xs text-[#3B352F]/80">Vi tager udgangspunkt i det, du kan lige nu.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Fokus på energi</div>
              <p className="text-xs text-[#3B352F]/80">Vi ser på belastning, restitution, krav, pauser og energi.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Små næste skridt</div>
              <p className="text-xs text-[#3B352F]/80">Målet er realistiske og overskuelige næste skridt.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Så er det vigtigt med et <strong>nænsomt tempo</strong>. Fokus vil typisk være belastning, <strong>restitution, krav, pauser, energi</strong> og <strong>realistiske næste skridt</strong>.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/stress" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Stress og belastning &rarr;
            </Link>
            <Link to="/soevnproblemer" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Søvnproblemer &rarr;
            </Link>
          </div>
        </div>
      )
    },
    {
      question: "Hvad koster et forløb?",
      answer: (
        <div className="space-y-5">
          <div className="inline-flex items-center gap-2 bg-[#123F13]/10 text-[#123F13] px-3 py-1.5 rounded-lg text-sm font-bold">
            <span className="uppercase text-xs tracking-wider opacity-75">Kort svar:</span>
            <span>Fra 8.500 kr.</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Pris fra 8.500 kr.</div>
              <p className="text-xs text-[#3B352F]/80">Et autismevenligt forløb starter her.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Afhænger af dit behov</div>
              <p className="text-xs text-[#3B352F]/80">Pris og omfang afhænger af behov, mål og antal sessioner.</p>
            </div>
            <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#E8DDCE]/40">
              <div className="font-bold text-[#123F13] text-sm mb-1">Første session afklarer</div>
              <p className="text-xs text-[#3B352F]/80">Første session kan bruges til at finde den rette løsning.</p>
            </div>
          </div>
          <p className="text-[#3B352F]/80 text-sm leading-relaxed pt-1">
            Et autismevenligt forløb <strong>starter fra 8.500 kr.</strong> Det konkrete forløb <strong>afhænger af behov, mål og antal sessioner</strong>. Første session kan bruges til at afklare dette.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E8DDCE]/30">
            <span className="text-xs font-bold text-[#3B352F]/50 uppercase tracking-wider mr-1">Læs videre:</span>
            <Link to="/priser" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Se aktuelle priser &rarr;
            </Link>
            <Link to="/bestil-tid" className="inline-flex items-center gap-1 text-xs font-bold text-[#8C2F12] hover:underline bg-[#8C2F12]/5 px-2.5 py-1 rounded-md">
              Book første session &rarr;
            </Link>
          </div>
        </div>
      )
    }
  ];

  // FAQ Schema til Google (JSON-LD)
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MedicalWebPage",
        "@id": "https://corehypnose.dk/autismevenlig-terapi",
        "name": "Autismevenlig terapi og forløb i København | Core Hypnose",
        "description": "Få et skræddersyet, autismevenligt terapiforløb i Valby eller online. Terapi tilpasset dine sensoriske og kognitive behov med fokus på ro, struktur og mental trivsel.",
        "medicalAudience": "Patient",
        "relevantSpecialty": "Psychiatry",
        "mainEntity": {
          "@type": "MedicalCondition",
          "name": "Autisme",
          "alternateName": "Autismespektrumforstyrrelse",
          "possibleTreatment": [
            {
              "@type": "MedicalTherapy",
              "name": "Metakognitiv Terapi"
            },
            {
              "@type": "MedicalTherapy",
              "name": "Hypnose"
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": typeof faq.answer === 'string' 
              ? faq.answer 
              : "Få et autismevenligt forløb tilpasset dine sensoriske og kognitive behov. Vi tilbyder første session til 375 kr., mulighed for online sessioner, dæmpet belysning, ingen krav om øjenkontakt og Core-rabat med lægehenvisning."
          }
        }))
      }
    ]
  };

  return (
    <ServiceLayout 
      title="Autismevenlige sessioner & forløb"
      subtitle="Oplever du overstimulering, social udmattelse eller kognitiv overbelastning? Få et skræddersyet terapiforløb tilpasset dit nervesystem – i Valby eller online."
      description="Et autismevenligt forløb hos Core Hypnose er skabt til dig, der er på autismespektret (ASF, Aspergers syndrom eller lignende), eller som oplever lignende sensoriske og kognitive udfordringer. Vi fjerner de barrierer, der ofte gør traditionel terapi udmattende, og arbejder i et roligt, struktureret tempo uden krav om øjenkontakt eller smalltalk."
      image="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/behandling-autisme.png"
      metaTitle="Autismevenlig Terapi i København | Metakognitiv & Hypnose"
      metaDescription="Få et skræddersyet, autismevenligt terapiforløb i Valby eller online. Tilpasset dine sensoriske behov uden krav om øjenkontakt. Første session kun 375 kr."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-5 py-3.5 rounded-lg font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første session (375 kr)
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-5 py-3.5 rounded-lg font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Ring på 27 20 46 71
          </a>
        </>
      }
      showContentSection={false}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Sektion: Når terapi i sig selv bliver krævende */}
      <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-white py-16 md:py-24 border-y border-[#E8DDCE] px-6 mb-20 mt-16 md:mt-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* LEFT SIDE: Header Block (5 Columns) */}
            <div className="lg:col-span-5 space-y-4">
            {/* Kicker with horizontal accent line */}
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#8C2F12]" aria-hidden="true"></span>
              <span className="text-xs font-bold tracking-widest uppercase text-[#123F13]">
                Når terapi i sig selv bliver krævende
              </span>
            </div>
            
            {/* Main Title */}
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.15] text-[#123F13]">
              Måske bruger du allerede meget energi på at aflæse situationen
            </h2>
          </div>

          {/* RIGHT SIDE: Info Grid & Links (7 Columns) */}
          <div className="lg:col-span-7 space-y-10">
            
            {/* Info Sub-Grid (3 rows, 2 columns) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              
              {/* Item 1 */}
              <div className="pb-6 border-b border-[#E8DDCE]">
                <h3 className="text-[15px] font-bold text-[#123F13] mb-2 tracking-wide">Uklare spørgsmål</h3>
                <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Det kan være svært at vide, hvad terapeuten egentlig spørger om.</p>
              </div>

              {/* Item 2 */}
              <div className="pb-6 border-b border-[#E8DDCE]">
                <h3 className="text-[15px] font-bold text-[#123F13] mb-2 tracking-wide">For mange overraskelser</h3>
                <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Ændringer og uforudsigelighed kan stjæle fokus fra selve behandlingen.</p>
              </div>

              {/* Item 3 */}
              <div className="pb-6 border-b border-[#E8DDCE]">
                <h3 className="text-[15px] font-bold text-[#123F13] mb-2 tracking-wide">Socialt pres</h3>
                <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Øjenkontakt, smalltalk eller forventningen om at reagere &ldquo;rigtigt&rdquo; kan tage energi.</p>
              </div>

              {/* Item 4 */}
              <div className="pb-6 border-b border-[#E8DDCE]">
                <h3 className="text-[15px] font-bold text-[#123F13] mb-2 tracking-wide">For meget på én gang</h3>
                <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Mange forklaringer, følelser og øvelser på samme tid kan blive overvældende.</p>
              </div>

              {/* Item 5 */}
              <div className="pb-6 border-b border-[#E8DDCE] md:border-b-0">
                <h3 className="text-[15px] font-bold text-[#123F13] mb-2 tracking-wide">Svært ved at svare hurtigt</h3>
                <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Du kan have brug for mere tid til at formulere et svar.</p>
              </div>

              {/* Item 6 */}
              <div className="pb-6 border-b border-[#E8DDCE] md:border-b-0">
                <h3 className="text-[15px] font-bold text-[#123F13] mb-2 tracking-wide">Usikkerhed bagefter</h3>
                <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Det kan være svært at huske, hvad der blev aftalt, eller hvad du skal gøre nu.</p>
              </div>
              
            </div>

            {/* Accent Blockquote Callout */}
            <div className="border-l-4 border-[#8C2F12] pl-6 py-1">
              <blockquote className="text-[16px] md:text-[17px] font-normal leading-relaxed text-[#3B352F]/90">
                En autismevenlig session handler ikke om at gøre dig mere &ldquo;normal&rdquo;. Den handler om at gøre behandlingen mere tilgængelig for dig.
              </blockquote>
            </div>

            {/* Meta Footer Links */}
            <footer className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs font-bold uppercase tracking-wider pt-4 border-t border-[#8C2F12]">
              <span className="text-[#3B352F]/60">Læs også</span>
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/angst" className="text-[#123F13] hover:text-[#8C2F12] hover:underline decoration-[#8C2F12] transition-all flex items-center gap-1.5">
                  Angst <span className="text-[#8C2F12] font-light" aria-hidden="true">&rarr;</span>
                </Link>
                <Link to="/stress" className="text-[#123F13] hover:text-[#8C2F12] hover:underline decoration-[#8C2F12] transition-all flex items-center gap-1.5">
                  Stress <span className="text-[#8C2F12] font-light" aria-hidden="true">&rarr;</span>
                </Link>
                <Link to="/tankemylder" className="text-[#123F13] hover:text-[#8C2F12] hover:underline decoration-[#8C2F12] transition-all flex items-center gap-1.5">
                  Tankemylder <span className="text-[#8C2F12] font-light" aria-hidden="true">&rarr;</span>
                </Link>
              </div>
            </footer>

          </div>
        </div>
      </div>
    </section>



      {/* Sektion: Min måde at tilpasse sessionen på */}
      <div className="mb-20">
        {/* Top del: Titel til venstre, tekst til højre */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-8 h-[2px] bg-[#8C2F12]" aria-hidden="true"></span>
              <span className="text-xs font-bold tracking-widest uppercase text-[#123F13]">
                Min måde at tilpasse sessionen på
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight">
              Mere tydelighed.<br />Mindre gætteri.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="text-[#3B352F]/80 text-lg leading-relaxed lg:text-right">
              Jeg forsøger at gøre det lettere for dig at bruge energien på det, du er kommet for, i stedet for på at afkode rummet, terapeuten eller skjulte forventninger.
            </p>
          </div>
        </div>

        {/* De 6 kasser under uden ikoner */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-[#E8DDCE]/60 p-6 md:p-8 rounded-2xl hover:border-[#8C2F12]/20 transition-all shadow-sm">
            <div className="w-8 h-[2px] bg-[#8C2F12] mb-3"></div>
            <h3 className="text-lg font-bold text-[#123F13] mb-2">Tydelige rammer</h3>
            <p className="text-[#3B352F]/70 text-sm leading-relaxed">
              Jeg gør det klart, hvad sessionen handler om, og hvad der skal ske.
            </p>
          </div>

          <div className="bg-white border border-[#E8DDCE]/60 p-6 md:p-8 rounded-2xl hover:border-[#8C2F12]/20 transition-all shadow-sm">
            <div className="w-8 h-[2px] bg-[#8C2F12] mb-3"></div>
            <h3 className="text-lg font-bold text-[#123F13] mb-2">Konkret sprog</h3>
            <p className="text-[#3B352F]/70 text-sm leading-relaxed">
              Jeg prøver at spørge direkte og forklare uden unødige omskrivninger.
            </p>
          </div>

          <div className="bg-white border border-[#E8DDCE]/60 p-6 md:p-8 rounded-2xl hover:border-[#8C2F12]/20 transition-all shadow-sm">
            <div className="w-8 h-[2px] bg-[#8C2F12] mb-3"></div>
            <h3 className="text-lg font-bold text-[#123F13] mb-2">Roligt tempo</h3>
            <p className="text-[#3B352F]/70 text-sm leading-relaxed">
              Du behøver ikke svare hurtigt. Der må gerne være pauser og tid til at tænke.
            </p>
          </div>

          <div className="bg-white border border-[#E8DDCE]/60 p-6 md:p-8 rounded-2xl hover:border-[#8C2F12]/20 transition-all shadow-sm">
            <div className="w-8 h-[2px] bg-[#8C2F12] mb-3"></div>
            <h3 className="text-lg font-bold text-[#123F13] mb-2">Færre sociale krav</h3>
            <p className="text-[#3B352F]/70 text-sm leading-relaxed">
              Du behøver ikke holde øjenkontakt eller være på en bestemt måde for at deltage.
            </p>
          </div>

          <div className="bg-white border border-[#E8DDCE]/60 p-6 md:p-8 rounded-2xl hover:border-[#8C2F12]/20 transition-all shadow-sm">
            <div className="w-8 h-[2px] bg-[#8C2F12] mb-3"></div>
            <h3 className="text-lg font-bold text-[#123F13] mb-2">Mulighed for pauser</h3>
            <p className="text-[#3B352F]/70 text-sm leading-relaxed">
              Jeg kan sætte tempoet ned, holde en kort pause eller ændre form, hvis det bliver for meget.
            </p>
          </div>

          <div className="bg-white border border-[#E8DDCE]/60 p-6 md:p-8 rounded-2xl hover:border-[#8C2F12]/20 transition-all shadow-sm">
            <div className="w-8 h-[2px] bg-[#8C2F12] mb-3"></div>
            <h3 className="text-lg font-bold text-[#123F13] mb-2">Tydeligt næste skridt</h3>
            <p className="text-[#3B352F]/70 text-sm leading-relaxed">
              Jeg hjælper med at samle op, så du ved, hvad vi har aftalt, og hvad der sker næste gang.
            </p>
          </div>
        </div>
      </div>

      {/* Visuel Før / Under / Efter Model */}
      <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-white py-16 md:py-24 border-y border-[#E8DDCE] px-6 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* LEFT SIDE: Header Block (5 Columns) */}
          <div className="lg:col-span-5 space-y-4">
            {/* Kicker with horizontal accent line */}
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#8C2F12]" aria-hidden="true"></span>
              <span className="text-xs font-bold tracking-widest uppercase text-[#123F13]">
                Før, under og efter
              </span>
            </div>
            
            {/* Main Title */}
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.15] text-[#123F13]">
              Et forløb med mere forudsigelighed
            </h2>
            
            {/* Description */}
            <p className="text-[#3B352F]/80 text-lg leading-relaxed pt-2">
              Tilpasningerne behøver ikke være store. Ofte er det de små ting, der gør sessionen lettere at være i.
            </p>
          </div>

          {/* RIGHT SIDE: Points (7 Columns) */}
          <div className="lg:col-span-7 space-y-8">
            
            {/* Før */}
            <div className="relative pl-12 pb-8 border-l-2 border-[#8C2F12]">
              {/* Step indicator */}
              <div className="absolute left-0 -translate-x-1/2 top-0 px-3 py-1 rounded-full bg-[#8C2F12] text-white text-[10px] font-bold uppercase tracking-wider">
                Før
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2 font-medium">
                  Du kan vide, hvad du kommer ind til
                </h3>
                <p className="text-[#3B352F]/70 text-sm md:text-base leading-relaxed">
                  Du kan få praktisk information om sessionen, og vi kan aftale særlige behov på forhånd. Du behøver ikke møde op og håbe, at du kan finde ud af resten undervejs.
                </p>
              </div>
            </div>

            {/* Under */}
            <div className="relative pl-12 pb-8 border-l-2 border-[#8C2F12]">
              {/* Step indicator */}
              <div className="absolute left-0 -translate-x-1/2 top-0 px-3 py-1 rounded-full bg-[#123F13] text-white text-[10px] font-bold uppercase tracking-wider">
                Under
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2 font-medium">
                  Jeg arbejder tydeligt og i et tempo, der giver mening
                </h3>
                <p className="text-[#3B352F]/70 text-sm md:text-base leading-relaxed">
                  Jeg forklarer, hvad vi gør og hvorfor. Du må gerne spørge, sige stop, bede om en pause eller sige, at et spørgsmål er uklart.
                </p>
              </div>
            </div>

            {/* Efter */}
            <div className="relative pl-12">
              {/* Step indicator */}
              <div className="absolute left-0 -translate-x-1/2 top-0 px-3 py-1 rounded-full bg-[#8C2F12] text-white text-[10px] font-bold uppercase tracking-wider">
                Efter
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2 font-medium">
                  Du ved, hvad næste skridt er
                </h3>
                <p className="text-[#3B352F]/70 text-sm md:text-base leading-relaxed">
                  Jeg samler op på det vigtigste og gør næste skridt konkret. Det kan være en aftale, en øvelse eller en tydelig retning for den næste session.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

      {/* Sektion: Metakognitiv Terapi & Hypnose */}
      <div className="mb-20">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">Core Hypnoses samlede metode</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13]">Metakognitiv terapi og hypnose – tilpasset dig</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          {/* Metakognitiv Terapi */}
          <div className="bg-white p-8 rounded-3xl border border-[#E8DDCE] flex flex-col justify-between">
            <div>
              <span className="text-xs font-bold tracking-wider uppercase text-[#8C2F12] block mb-2">Metakognitiv terapi</span>
              <h3 className="text-2xl font-serif text-[#123F13] mb-4 font-medium">
                Mindre kamp med tanker og mindre tid fanget i dem
              </h3>
              <p className="text-[#3B352F]/80 leading-relaxed text-sm md:text-base mb-6">
                Jeg arbejder med, hvordan du forholder dig til tanker, bekymring, grubleri og opmærksomhed. Du behøver ikke analysere alle tanker eller finde det perfekte svar på dem.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex gap-3 items-start text-[#3B352F]/80 text-sm md:text-base">
                  <CheckCircle2 className="text-[#8C2F12] w-5 h-5 shrink-0 mt-0.5" />
                  <span>konkrete forklaringer på, hvad vi arbejder med</span>
                </li>
                <li className="flex gap-3 items-start text-[#3B352F]/80 text-sm md:text-base">
                  <CheckCircle2 className="text-[#8C2F12] w-5 h-5 shrink-0 mt-0.5" />
                  <span>øvelser i fleksibel opmærksomhed</span>
                </li>
                <li className="flex gap-3 items-start text-[#3B352F]/80 text-sm md:text-base">
                  <CheckCircle2 className="text-[#8C2F12] w-5 h-5 shrink-0 mt-0.5" />
                  <span>mindre tid brugt på bekymring og analyse</span>
                </li>
                <li className="flex gap-3 items-start text-[#3B352F]/80 text-sm md:text-base">
                  <CheckCircle2 className="text-[#8C2F12] w-5 h-5 shrink-0 mt-0.5" />
                  <span>fokus på det, der holder problemet i gang nu</span>
                </li>
              </ul>
            </div>

            <div>
              <Link 
                to="/metakognitiv-terapi" 
                className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:underline group"
              >
                Læs mere om metakognitiv terapi 
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {/* Hypnose */}
          <div className="bg-white p-8 rounded-3xl border border-[#E8DDCE] flex flex-col justify-between">
            <div>
              <span className="text-xs font-bold tracking-wider uppercase text-[#8C2F12] block mb-2">Hypnose</span>
              <h3 className="text-2xl font-serif text-[#123F13] mb-4 font-medium">
                Ro, fokuseret opmærksomhed og nye erfaringer
              </h3>
              <p className="text-[#3B352F]/80 leading-relaxed text-sm md:text-base mb-6">
                Hypnose kan bruges som støtte til ro, indre billeder og nye erfaringer. Du er ikke væk, og du mister ikke kontrollen. Jeg forklarer altid, hvad der skal ske.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex gap-3 items-start text-[#3B352F]/80 text-sm md:text-base">
                  <CheckCircle2 className="text-[#8C2F12] w-5 h-5 shrink-0 mt-0.5" />
                  <span>ingen mystik eller showpræg</span>
                </li>
                <li className="flex gap-3 items-start text-[#3B352F]/80 text-sm md:text-base">
                  <CheckCircle2 className="text-[#8C2F12] w-5 h-5 shrink-0 mt-0.5" />
                  <span>du kan sige stop eller åbne øjnene når som helst</span>
                </li>
                <li className="flex gap-3 items-start text-[#3B352F]/80 text-sm md:text-base">
                  <CheckCircle2 className="text-[#8C2F12] w-5 h-5 shrink-0 mt-0.5" />
                  <span>guidningen kan være konkret og enkel</span>
                </li>
                <li className="flex gap-3 items-start text-[#3B352F]/80 text-sm md:text-base">
                  <CheckCircle2 className="text-[#8C2F12] w-5 h-5 shrink-0 mt-0.5" />
                  <span>metoden tilpasses din måde at være opmærksom på</span>
                </li>
              </ul>
            </div>

            <div>
              <Link 
                to="/hypnose" 
                className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:underline group"
              >
                Læs mere om hypnose 
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Sektion: Du må gerne fortælle mig, hvad der hjælper */}
      <div className="mb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* LEFT SIDE: Header Block (5 Columns) */}
          <div className="lg:col-span-5 space-y-4">
            {/* Kicker with horizontal accent line */}
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#8C2F12]" aria-hidden="true"></span>
              <span className="text-xs font-bold tracking-widest uppercase text-[#123F13]">
                Du må gerne fortælle mig, hvad der hjælper
              </span>
            </div>
            
            {/* Main Title */}
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.15] text-[#123F13]">
              Det er ikke dig, der skal passe ind i sessionen
            </h2>

            <p className="text-[#3B352F]/80 text-lg leading-relaxed pt-2">
              Nogle har brug for meget struktur. Andre vil helst have færre ord. Nogle vil vide alt på forhånd. Andre kun det vigtigste. Det kan du fortælle mig om.
            </p>
          </div>

          {/* RIGHT SIDE: Points (7 Columns) */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              
              {/* Item 1 */}
              <div className="flex gap-4 items-start pb-6 border-b border-[#E8DDCE]">
                <span className="text-[#8C2F12] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <div>
                  <h3 className="text-[15px] font-bold text-[#123F13] mb-1 tracking-wide">Mindre smalltalk</h3>
                  <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Jeg kan gå mere direkte til det, du er kommet for.</p>
                </div>
              </div>

              {/* Item 2 */}
              <div className="flex gap-4 items-start pb-6 border-b border-[#E8DDCE]">
                <span className="text-[#8C2F12] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <div>
                  <h3 className="text-[15px] font-bold text-[#123F13] mb-1 tracking-wide">Ingen krav om øjenkontakt</h3>
                  <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Du må kigge væk, lukke øjnene eller sidde på den måde, der fungerer bedst.</p>
                </div>
              </div>

              {/* Item 3 */}
              <div className="flex gap-4 items-start pb-6 border-b border-[#E8DDCE]">
                <span className="text-[#8C2F12] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <div>
                  <h3 className="text-[15px] font-bold text-[#123F13] mb-1 tracking-wide">Ekstra betænkningstid</h3>
                  <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Du må gerne bruge tid på at finde et svar.</p>
                </div>
              </div>

              {/* Item 4 */}
              <div className="flex gap-4 items-start pb-6 border-b border-[#E8DDCE]">
                <span className="text-[#8C2F12] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <div>
                  <h3 className="text-[15px] font-bold text-[#123F13] mb-1 tracking-wide">Klare forklaringer</h3>
                  <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Du må gerne spørge: &ldquo;Hvad mener du helt konkret?&rdquo;</p>
                </div>
              </div>

              {/* Item 5 */}
              <div className="flex gap-4 items-start pb-6 border-b border-[#E8DDCE] md:border-b-0">
                <span className="text-[#8C2F12] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <div>
                  <h3 className="text-[15px] font-bold text-[#123F13] mb-1 tracking-wide">Pauser og ændringer undervejs</h3>
                  <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Jeg kan stoppe op, sænke tempoet eller gøre noget mere enkelt.</p>
                </div>
              </div>

              {/* Item 6 */}
              <div className="flex gap-4 items-start pb-6 border-b border-[#E8DDCE] md:border-b-0">
                <span className="text-[#8C2F12] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <div>
                  <h3 className="text-[15px] font-bold text-[#123F13] mb-1 tracking-wide">Valby eller online</h3>
                  <p className="text-[14px] leading-relaxed text-[#3B352F]/70">Du kan vælge den ramme, der er mest overskuelig for dig.</p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Sektion: Første behandlingssession */}
      <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-gradient-to-br from-[#123F13] to-[#173F35] text-white py-16 md:py-24 px-6 mb-20">
        <div className="max-w-6xl mx-auto space-y-10">
          
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="flex items-center justify-center gap-3">
              <span className="w-8 h-[2px] bg-[#b59b5a]" aria-hidden="true"></span>
              <span className="text-xs font-bold tracking-widest uppercase text-[#b59b5a]">
                Første behandlingssession
              </span>
              <span className="w-8 h-[2px] bg-[#b59b5a]" aria-hidden="true"></span>
            </div>
            
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.15] text-white">
              Første behandlingssession i Valby eller online
            </h2>
            
            <p className="text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
              Den første session handler om at skabe overblik. Vi taler om, hvad du oplever, hvad der er svært, og hvilke rammer der kan gøre behandlingen mere overskuelig for dig.
            </p>
          </div>

          {/* The 3 stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-8 border-y border-white/10 text-center">
            <div>
              <div className="text-3xl font-serif font-bold text-[#b59b5a]">375 kr.</div>
              <div className="text-xs uppercase tracking-wider text-white/70 font-semibold mt-1">første session</div>
            </div>
            <div>
              <div className="text-3xl font-serif font-bold text-white">50 minutter</div>
              <div className="text-xs uppercase tracking-wider text-white/70 font-semibold mt-1">personlig afklaring</div>
            </div>
            <div>
              <div className="text-3xl font-serif font-bold text-white">Valby eller online</div>
              <div className="text-xs uppercase tracking-wider text-white/70 font-semibold mt-1">du vælger rammen</div>
            </div>
          </div>

          {/* What you get out of the session */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 space-y-6">
            <h3 className="text-xl font-serif font-bold text-[#b59b5a] text-center">Hvad du får ud af sessionen:</h3>
            
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <li className="flex gap-3 items-start text-white/90 text-sm md:text-base">
                <span className="text-[#b59b5a] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <span>Personlig kortlægning af det, der fylder</span>
              </li>
              <li className="flex gap-3 items-start text-white/90 text-sm md:text-base">
                <span className="text-[#b59b5a] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <span>Tydelig afklaring af næste skridt</span>
              </li>
              <li className="flex gap-3 items-start text-white/90 text-sm md:text-base">
                <span className="text-[#b59b5a] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <span>Guidede øvelser og praktiske teknikker</span>
              </li>
              <li className="flex gap-3 items-start text-white/90 text-sm md:text-base">
                <span className="text-[#b59b5a] text-xl font-bold shrink-0 mt-0.5" aria-hidden="true">✓</span>
                <span>En klar personlig behandlingsplan</span>
              </li>
            </ul>

            <p className="text-xs text-white/60 italic pt-4 border-t border-white/10 text-center">
              Betaling sker altid inden sessionen starter.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
            <Link 
              to="/bestil-tid" 
              className="inline-flex items-center justify-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-xl font-bold text-base hover:bg-[#a33a1a] transition-all shadow-md hover:scale-[1.02]"
            >
              Book første behandlingssession <ArrowRight size={18} />
            </Link>
            <Link 
              to="/priser" 
              className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white px-8 py-4 rounded-xl font-bold text-base hover:bg-white/20 transition-all shadow-md hover:scale-[1.02]"
            >
              Se priser og vilkår <ArrowRight size={18} />
            </Link>
          </div>

        </div>
      </section>

      {/* Sektion: Priser, rabat og vilkår */}
      <div className="mb-20">
        {/* Top Row: Titel til venstre, tekst til højre */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-12">
          <div className="lg:col-span-5 space-y-3">
            <span className="text-[#123F13] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
              <span className="w-8 h-[2px] bg-[#8C2F12]"></span>
              Priser, rabat og vilkår
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#123F13] leading-tight">
              Det vigtigste<br />samlet ét sted
            </h2>
          </div>
          <div className="lg:col-span-7 lg:text-right">
            <p className="text-[#3B352F]/80 text-lg leading-relaxed max-w-2xl lg:ml-auto">
              Du kan starte med én første behandlingssession. Hvis du fortsætter, kan du vælge almindelige sessioner, rabat til studerende og pensionister eller et klippekort. Her kan du også se Core-rabatten ved lægehenvisning og den vigtigste afbudsregel.
            </p>
          </div>
        </div>

        {/* Bottom Row: Prisoversigt, Core-rabat, Afbud og ændringer under */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
          
          {/* Venstre kolonne: Core-rabat og Afbud */}
          <div className="lg:col-span-5 flex flex-col justify-between gap-6">
            <div className="space-y-6">
              {/* Core-rabat Card */}
              <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 space-y-3">
                <div className="flex items-center gap-2 text-[#8C2F12]">
                  <i className="fa-solid fa-percentage text-lg" aria-hidden="true"></i>
                  <h3 className="font-serif font-bold text-lg text-[#123F13]">Core-rabat ved lægehenvisning</h3>
                </div>
                <p className="text-sm font-bold text-[#8C2F12]">300 kr. i rabat på en almindelig session</p>
                <p className="text-sm leading-relaxed text-[#3B352F]/70">
                  Har du en lægehenvisning, kan den almindelige sessionspris være 1.500 kr. i stedet for 1.800 kr.
                </p>
              </div>

              {/* Afbud Card */}
              <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 space-y-3">
                <div className="flex items-center gap-2 text-[#8C2F12]">
                  <i className="fa-solid fa-calendar-xmark text-lg" aria-hidden="true"></i>
                  <h3 className="font-serif font-bold text-lg text-[#123F13]">Afbud og ændringer</h3>
                </div>
                <p className="text-sm font-bold text-[#8C2F12]">Giv besked senest 24 timer før</p>
                <p className="text-sm leading-relaxed text-[#3B352F]/70">
                  Ved senere afbud koster det ét klip eller fuld pris. Reglen gælder også ved akut sygdom. Kan du ikke møde op i klinikken, tilbyder jeg altid, at den planlagte session gennemføres online i stedet.
                </p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3 pt-2">
              <Link 
                to="/priser" 
                className="inline-flex items-center justify-center gap-2 bg-[#123F13] text-white px-6 py-3.5 rounded-xl font-bold text-base hover:bg-[#1b4f1c] transition-all shadow-md w-full"
              >
                Se alle priser og vilkår <ArrowRight size={18} />
              </Link>
              <a 
                href="tel:+4527204671" 
                className="inline-flex items-center justify-center gap-2 bg-white border border-[#E8DDCE] text-[#123F13] px-6 py-3.5 rounded-xl font-bold text-base hover:bg-[#FAF7F2] transition-all shadow-sm w-full"
              >
                <i className="fa-solid fa-phone text-[#8C2F12]" aria-hidden="true"></i>
                Ring 27 20 46 71
              </a>
            </div>
          </div>

          {/* RIGHT SIDE: Price List & CTA (7 Columns) */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 md:p-8 space-y-6 shadow-sm">
              <h3 className="text-xl font-serif font-bold text-[#123F13] pb-4 border-b border-[#E8DDCE]">
                Prisoversigt
              </h3>
              
              <div className="space-y-6">
                {/* Price Item 1 */}
                <div className="pb-6 border-b border-[#FAF7F2] last:border-0 last:pb-0">
                  <div className="flex justify-between items-baseline gap-4">
                    <h4 className="text-lg font-bold text-[#123F13]">Første behandlingssession</h4>
                    <span className="text-xl font-serif font-bold text-[#8C2F12] shrink-0">375 kr.</span>
                  </div>
                  <p className="text-sm text-[#3B352F]/70 mt-1">
                    50 minutter &middot; kun for nye klienter &middot; Valby eller online.
                  </p>
                </div>

                {/* Price Item 2 */}
                <div className="pb-6 border-b border-[#FAF7F2] last:border-0 last:pb-0">
                  <div className="flex justify-between items-baseline gap-4">
                    <h4 className="text-lg font-bold text-[#123F13]">Almindelig session</h4>
                    <span className="text-xl font-serif font-bold text-[#123F13] shrink-0">1.800 kr.</span>
                  </div>
                  <p className="text-sm text-[#3B352F]/70 mt-1">
                    50 minutter &middot; individuel session i Valby eller online.
                  </p>
                </div>

                {/* Price Item 3 */}
                <div className="pb-6 border-b border-[#FAF7F2] last:border-0 last:pb-0">
                  <div className="flex justify-between items-baseline gap-4">
                    <h4 className="text-lg font-bold text-[#123F13]">Studerende og pensionister</h4>
                    <span className="text-xl font-serif font-bold text-[#123F13] shrink-0">1.350 kr.</span>
                  </div>
                  <p className="text-sm text-[#3B352F]/70 mt-1">
                    50 minutter &middot; 25 % rabat på den almindelige sessionspris.
                  </p>
                </div>

                {/* Price Item 4 */}
                <div className="pb-6 border-b border-[#FAF7F2] last:border-0 last:pb-0">
                  <div className="flex justify-between items-baseline gap-4">
                    <h4 className="text-lg font-bold text-[#123F13]">Klippekort &middot; 5 sessioner</h4>
                    <span className="text-xl font-serif font-bold text-[#123F13] shrink-0">8.500 kr.</span>
                  </div>
                  <p className="text-sm text-[#3B352F]/70 mt-1">
                    Flere sessioner samlet i ét forløb.
                  </p>
                </div>

                {/* Price Item 5 */}
                <div className="pb-6 border-b border-[#FAF7F2] last:border-0 last:pb-0">
                  <div className="flex justify-between items-baseline gap-4">
                    <h4 className="text-lg font-bold text-[#123F13]">Klippekort &middot; 10 sessioner</h4>
                    <span className="text-xl font-serif font-bold text-[#123F13] shrink-0">15.000 kr.</span>
                  </div>
                  <p className="text-sm text-[#3B352F]/70 mt-1">
                    Til et længere og mere sammenhængende forløb.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Vælg den ramme, der passer bedst */}
      <section className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-white py-16 md:py-24 border-y border-[#E8DDCE] px-6 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#8C2F12]"></span>
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Vælg den ramme, der passer bedst</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] leading-tight">
              Session i Valby eller online
            </h2>
            <p className="text-[#3B352F]/80 text-lg leading-relaxed">
              Nogle foretrækker at komme fysisk i klinikken. Andre har mere ro og overskud hjemmefra. Begge dele er muligt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* I klinikken i Valby */}
            <div className="bg-[#FAF7F2] border border-[#E8DDCE] p-8 rounded-3xl flex flex-col justify-between hover:shadow-md transition-all">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-[#123F13] mb-6 shadow-sm border border-[#E8DDCE]/50">
                  <MapPin size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#123F13] mb-3">I klinikken i Valby</h3>
                <p className="text-[#3B352F]/70 leading-relaxed mb-6">
                  Core Hypnose ligger på Valby Tingsted 7, 2500 Valby. Her kan du møde op fysisk i rolige rammer.
                </p>
              </div>
              <div>
                <Link to="/kontakt" className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:text-[#a33a1a] transition-colors group">
                  Se kontakt og praktisk info <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Online session */}
            <div className="bg-[#FAF7F2] border border-[#E8DDCE] p-8 rounded-3xl flex flex-col justify-between hover:shadow-md transition-all">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-[#123F13] mb-6 shadow-sm border border-[#E8DDCE]/50">
                  <Laptop size={24} />
                </div>
                <h3 className="text-xl font-bold text-[#123F13] mb-3">Online session</h3>
                <p className="text-[#3B352F]/70 leading-relaxed mb-6">
                  Du kan deltage hjemmefra et roligt sted med en stabil forbindelse. For nogle gør det sessionen mere overskuelig.
                </p>
              </div>
              <div>
                <Link to="/online-sessioner" className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:text-[#a33a1a] transition-colors group">
                  Læs om online sessioner <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: 10 Store Visuelle FAQ-svar */}
      <div className="mb-20">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">Spørgsmål & Svar</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13]">Ofte stillede spørgsmål om autismevenlig terapi</h2>
          <p className="text-[#3B352F]/70 mt-4 text-lg">
            Her finder du uddybende svar på de mest almindelige spørgsmål om vores autismevenlige forløb, rammer og metoder.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className="space-y-4">
            {faqData.slice(0, 5).map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaqIndex === index}
                onToggle={() => toggleFaq(index)}
              />
            ))}
          </div>
          <div className="space-y-4">
            {faqData.slice(5, 10).map((faq, index) => {
              const actualIndex = index + 5;
              return (
                <FAQItem
                  key={actualIndex}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFaqIndex === actualIndex}
                  onToggle={() => toggleFaq(actualIndex)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Sektion: Læs også / Relevante sider */}
      <div className="mt-20 mb-12 pt-12 border-t border-[#E8DDCE]">
        <div className="space-y-8">
          {/* TOP SIDE: Title & Description */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#8C2F12]" aria-hidden="true"></span>
              <span className="text-xs font-bold tracking-widest uppercase text-[#123F13]">
                Læs også
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-[#123F13]">
              Relevante sider
            </h2>
            <p className="text-[#3B352F]/80 text-base leading-relaxed max-w-2xl">
              Her kan du læse mere om områder, der ofte hænger sammen med autistisk belastning.
            </p>
          </div>

          {/* BOTTOM SIDE: Links Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Angst", path: "/angst" },
              { label: "Stress", path: "/stress" },
              { label: "Tankemylder", path: "/tankemylder" },
              { label: "Søvnproblemer", path: "/soevnproblemer" },
              { label: "ADHD", path: "/adhd" },
              { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" },
              { label: "Hypnose", path: "/hypnose" },
              { label: "Online sessioner", path: "/online-sessioner" }
            ].map((link, idx) => (
              <Link
                key={idx}
                to={link.path}
                className="flex items-center justify-between bg-white hover:bg-[#FAF7F2] border border-[#E8DDCE] p-4 rounded-xl text-[#123F13] font-semibold hover:text-[#8C2F12] hover:border-[#8C2F12]/30 transition-all group"
              >
                <span>{link.label}</span>
                <span className="text-[#8C2F12] font-bold text-lg transition-transform group-hover:translate-x-1">
                  &rsaquo;
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Sektion: Et roligt første skridt */}
      <div className="mt-24 w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-gradient-to-br from-[#123F13] to-[#193f10] text-white py-16 md:py-20 overflow-hidden">
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-white/[0.03] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center relative z-10 flex flex-col items-center">
          <div className="mb-4">
            <img 
              src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png" 
              alt="Core Kvalitetsstempel" 
              className="w-24 h-24 rotate-[-6deg] drop-shadow-[0_10px_20px_rgba(0,0,0,0.3)] object-contain mx-auto"
            />
          </div>
          <span className="text-[#FAF7F2]/80 font-bold text-xs uppercase tracking-widest block mb-3">
            Et roligt første skridt
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif text-[#FAF7F2] mb-4 max-w-2xl mx-auto leading-tight font-semibold">
            Du behøver ikke vide præcis, hvad du har brug for, før du tager kontakt
          </h2>
          <p className="text-[#FAF7F2]/80 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Jeg kan begynde med at skabe overblik og finde ud af, hvilke rammer der hjælper dig. Målet er ikke, at du skal tilpasse dig terapien. Behandlingen skal give mening for dig.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6 w-full">
            <Link
              to="/bestil-tid"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-[#a33a1a] transition-all shadow-md hover:-translate-y-0.5"
            >
              Book første behandlingssession
            </Link>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-[#FAF7F2] text-[#FAF7F2] px-8 py-4 rounded-lg font-bold text-base hover:bg-white/10 transition-all"
            >
              Kontakt mig
            </Link>
          </div>
          <p className="text-[#FAF7F2]/70 text-sm">
            Du kan også ringe direkte på{" "}
            <a href="tel:+4527204671" className="text-[#FAF7F2] font-bold hover:underline">
              27 20 46 71
            </a>
          </p>
        </div>
      </div>
    </ServiceLayout>
  );
};

export default Autisme;
